/**
 * Client HTTP pour l'API
 * Wrapper autour de fetch avec intercepteurs pour auth
 *
 * Fix US-170 : auto-refresh JWT sur 401
 * Si le token expire (30min), on tente POST /auth/refresh automatiquement.
 * Si le refresh échoue, on redirige vers /login.
 */

import { API_CONFIG, API_ENDPOINTS } from '@/config/api';

const TOKEN_KEY = 'horaliia_token';
const REFRESH_TOKEN_KEY = 'horaliia_refresh_token';

// Endpoints publics : un 401 y signifie « identifiants refusés », pas « session expirée »
const NO_REFRESH_ENDPOINTS: readonly string[] = [API_ENDPOINTS.login, API_ENDPOINTS.refresh];

// Flag global : évite les appels refresh concurrents (plusieurs requêtes à 401 simultanément)
let _isRefreshing = false;
let _refreshQueue: Array<(token: string | null) => void> = [];

function _drainQueue(token: string | null): void {
  _refreshQueue.forEach(resolve => resolve(token));
  _refreshQueue = [];
}

export class ApiClient {
  private baseURL: string;
  private timeout: number;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.timeout = API_CONFIG.timeout;
  }

  private getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  protected getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  protected setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  protected clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Tente de rafraîchir le JWT via POST /auth/refresh.
   * Retourne le nouvel access_token, ou null si le refresh échoue.
   * Sérialise les appels concurrents via _refreshQueue.
   */
  private async _tryRefresh(): Promise<string | null> {
    if (_isRefreshing) {
      // Une autre requête est déjà en train de refresh — on attend
      return new Promise(resolve => { _refreshQueue.push(resolve); });
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    _isRefreshing = true;
    try {
      const url = `${this.baseURL}${API_ENDPOINTS.refresh}`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!resp.ok) {
        _drainQueue(null);
        return null;
      }

      const data = await resp.json();
      const newToken: string = data.access_token;
      const newRefresh: string = data.refresh_token ?? refreshToken;
      this.setTokens(newToken, newRefresh);
      _drainQueue(newToken);
      return newToken;
    } catch {
      _drainQueue(null);
      return null;
    } finally {
      _isRefreshing = false;
    }
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    _isRetry = false,  // flag interne — évite boucle infinie
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = { ...options, headers };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, { ...config, signal: controller.signal });
      clearTimeout(timeoutId);

      // ── Auto-refresh sur 401 ──────────────────────────────────────────────
      if (response.status === 401 && !_isRetry && !NO_REFRESH_ENDPOINTS.includes(endpoint)) {
        const newToken = await this._tryRefresh();
        if (newToken) {
          // Retry la requête originale avec le nouveau token
          return this.request<T>(endpoint, options, true);
        }
        // Refresh échoué → déconnexion + redirect login
        this.clearTokens();
        window.location.href = '/login';
        throw new Error('Session expirée — reconnexion requise');
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }

      if (response.status === 204) {
        return null as T;
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error');
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
